import type { Key } from 'ErgoBot';
import {
  getMusicXmlKeyContextsByMeasure,
  getParallelModeConversionPitchDelta,
  type KeyContext,
  type ParallelModeConversion,
} from 'Utils/MusicXmlModeConversion';

/** The pitch class of each letter C D E F G A B without an accidental. */
const NATURAL_PITCH_CLASSES: ReadonlyArray<number> = [0, 2, 4, 5, 7, 9, 11];
/** The letters a key signature sharpens, in order, as indices into CDEFGAB: F C G D A E B. */
const SHARPENED_IN_ORDER: ReadonlyArray<number> = [3, 0, 4, 1, 5, 2, 6];
/** The letters a key signature flattens, in order: B E A D G C F. */
const FLATTENED_IN_ORDER: ReadonlyArray<number> = [6, 2, 5, 1, 4, 0, 3];
const MIDDLE_C = 60;

/** What a key signature of `fifths` does to a letter: 1 sharpened, -1 flattened, 0 neither. */
const signatureAlteration = (letter: number, fifths: number): number => {
  if (fifths > 0) return SHARPENED_IN_ORDER.slice(0, fifths).includes(letter) ? 1 : 0;
  return FLATTENED_IN_ORDER.slice(0, -fifths).includes(letter) ? -1 : 0;
};

/** The key the phrase is written in: that of its first measure that states one. */
const writtenKeyOf = (musicXml: string): KeyContext | undefined => {
  const contexts = getMusicXmlKeyContextsByMeasure(musicXml);
  const firstMeasure = Math.min(...Array.from(contexts.keys()));
  return contexts.get(firstMeasure);
};

/**
 * What a phrase's written notes sound as, the way the fingering runtime takes
 * it: the transposition, and for a change of mode the semitones it moves each
 * letter's note of the written key by (the parallel minor of a phrase in C
 * major lowers E, A and B). A note the written key does not hold is moved with
 * its letter.
 */
export const soundingKeyOf = (
  musicXml: string | undefined,
  transposition: number,
  modeConversion: ParallelModeConversion,
): Key => {
  if (modeConversion === 'off' || !musicXml) return { transposition };
  const writtenKey = writtenKeyOf(musicXml);
  if (writtenKey === undefined) return { transposition };
  const alter = NATURAL_PITCH_CLASSES.map((pitchClass, letter) =>
    getParallelModeConversionPitchDelta(
      MIDDLE_C + pitchClass + signatureAlteration(letter, writtenKey.fifths),
      writtenKey,
      modeConversion,
    ),
  );
  return alter.some((semitones) => semitones !== 0) ? { transposition, alter } : { transposition };
};
